import { Box, Flex, Text } from "@/components/display/display-primitives";
import type { FeedItem } from "@/lib/display-feed";
import { resolveAssetUrl } from "@/lib/api";
import { useFitText } from "@/hooks/use-fit-text";

interface MessageSlideProps {
  item: FeedItem;
  isActive: boolean;
}

/**
 * Slide message : le texte occupe toujours le maximum d'espace disponible.
 *
 * Stratégie :
 *   - La zone de texte prend tout l'espace vertical (flex: 1) au-dessus
 *     de la signature.
 *   - useFitText() binary-search la taille de fonte optimale qui remplit
 *     cette zone sans déborder — recalculé sur chaque resize (ResizeObserver).
 *   - Le slide est déjà opacity: 0 quand inactif, ce qui masque le flash
 *     de calcul au montage.
 */
export function MessageSlide({ item, isActive }: MessageSlideProps) {
  const text = typeof item.data.text === "string" ? item.data.text : "";
  const author = typeof item.data.author_name === "string" ? item.data.author_name : "";
  const rawAvatarUrl =
    typeof item.data.author_avatar_url === "string" && item.data.author_avatar_url.trim()
      ? item.data.author_avatar_url
      : null;
  const avatarSrc = rawAvatarUrl ? resolveAssetUrl(rawAvatarUrl) : null;
  const hasSignature = Boolean(author) || Boolean(avatarSrc);

  const { containerRef, textRef } = useFitText({
    content: text,
    minPx: 18,
    maxPx: 220,
  });

  return (
    <Box
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: `clamp(2rem, 6vw, 5rem) clamp(2rem, 8vw, 7rem)`,
        opacity: isActive ? 1 : 0,
        transition: "opacity 1s ease-in-out",
        position: "absolute",
        inset: 0,
        background: "radial-gradient(ellipse at center, #1a1a2e 0%, #0d0d1a 100%)",
        boxSizing: "border-box",
      }}
    >
      {/* ── Zone texte : prend tout l'espace vertical disponible ── */}
      <Box
        ref={containerRef}
        style={{
          flex: 1,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
          // Padding interne pour les guillemets décoratifs
          paddingInline: "clamp(2rem, 5vw, 4rem)",
        }}
      >
        {/* Guillemet ouvrant */}
        <Text
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            fontSize: "clamp(4rem, 10vw, 8rem)",
            color: "rgba(255,255,255,0.07)",
            lineHeight: 0,
            fontFamily: "Georgia, serif",
            userSelect: "none",
            transform: "translateY(0.55em)",
            pointerEvents: "none",
          }}
        >
          {"“"}
        </Text>

        {/* Message — taille calculée dynamiquement par useFitText */}
        <p
          ref={textRef}
          style={{
            margin: 0,
            padding: 0,
            textAlign: "center",
            fontWeight: 300,
            color: "rgba(255,255,255,0.92)",
            lineHeight: 1.45,
            letterSpacing: "0.015em",
            fontFamily: "'Georgia', serif",
            // fontSize est injecté directement par useFitText via le ref
            // Valeur initiale raisonnable pour éviter un flash au montage :
            fontSize: "clamp(1.8rem, 5vw, 4rem)",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            hyphens: "auto",
          }}
        >
          {text}
        </p>

        {/* Guillemet fermant */}
        <Text
          aria-hidden
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            fontSize: "clamp(4rem, 10vw, 8rem)",
            color: "rgba(255,255,255,0.07)",
            lineHeight: 0,
            fontFamily: "Georgia, serif",
            userSelect: "none",
            transform: "translateY(0.15em)",
            pointerEvents: "none",
          }}
        >
          {"”"}
        </Text>
      </Box>

      {/* ── Signature : hauteur fixe, ne participe pas au fit ────── */}
      {hasSignature && (
        <Flex
          align="center"
          justify="center"
          gap="3"
          style={{
            flexShrink: 0,
            paddingTop: "clamp(1rem, 3vw, 2rem)",
          }}
        >
          {avatarSrc && (
            <img
              src={avatarSrc}
              alt={author || "avatar"}
              style={{
                width: 44,
                height: 44,
                opacity: 0.85,
                flexShrink: 0,
                borderRadius: "999px",
                objectFit: "cover",
                display: "block",
              }}
            />
          )}
          {author && (
            <Text
              as="span"
              style={{
                fontSize: "clamp(0.85rem, 1.8vw, 1.3rem)",
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              {author}
            </Text>
          )}
        </Flex>
      )}
    </Box>
  );
}
